import { useState } from 'react';

const experts = [
  {
    id: 1,
    name: 'Dr. Sarah Mitchell',
    role: 'GCSE Computer Science Specialist',
    board: 'AQA · OCR',
    experience: '12 years teaching',
    bio: 'Former AQA examiner with 12 years of classroom experience. Sarah specialises in helping students master algorithms, data representation and programming concepts for GCSE.',
    subjects: ['Algorithms', 'Programming', 'Data Representation'],
    rating: 4.9,
    students: 1240,
    image: 'https://readdy.ai/api/search-image?query=professional%20female%20teacher%20portrait%2C%20confident%20friendly%20smile%2C%20dark%20green%20blazer%2C%20clean%20white%20background%2C%20academic%20professional%20headshot%2C%20natural%20lighting%2C%2030s%20age%2C%20realistic%20photo&width=400&height=400&seq=expert1&orientation=squarish',
    available: true,
  },
  {
    id: 2,
    name: 'James Okafor',
    role: 'A Level CS & Programming Tutor',
    board: 'AQA · OCR',
    experience: '9 years teaching',
    bio: 'Computer Science graduate from UCL with 9 years of A Level teaching experience. James is passionate about making complex topics like theory of computation and data structures accessible.',
    subjects: ['Theory of Computation', 'Data Structures', 'Python'],
    rating: 4.8,
    students: 980,
    image: 'https://readdy.ai/api/search-image?query=professional%20male%20teacher%20portrait%2C%20confident%20friendly%20smile%2C%20dark%20blazer%2C%20clean%20white%20background%2C%20academic%20professional%20headshot%2C%20natural%20lighting%2C%2030s%20age%2C%20realistic%20photo&width=400&height=400&seq=expert2&orientation=squarish',
    available: true,
  },
  {
    id: 3,
    name: 'Priya Sharma',
    role: 'Learning Specialist & Exam Coach',
    board: 'All Boards',
    experience: '8 years coaching',
    bio: 'Priya combines her background in educational psychology with deep CS knowledge to help students develop effective revision strategies and exam technique for maximum marks.',
    subjects: ['Exam Technique', 'Networks', 'Cyber Security'],
    rating: 4.9,
    students: 1560,
    image: 'https://readdy.ai/api/search-image?query=professional%20female%20tutor%20portrait%2C%20warm%20friendly%20smile%2C%20smart%20casual%20attire%2C%20clean%20light%20background%2C%20academic%20professional%20headshot%2C%20natural%20lighting%2C%2030s%20age%2C%20realistic%20photo&width=400&height=400&seq=expert3&orientation=squarish',
    available: false,
  },
  {
    id: 4,
    name: 'Tom Whitfield',
    role: 'GCSE & A Level CS Teacher',
    board: 'Edexcel · WJEC',
    experience: '7 years teaching',
    bio: 'Tom specialises in Edexcel and WJEC specifications and has helped hundreds of students achieve grade 8 and 9. He focuses on computer systems, networks and ethical issues.',
    subjects: ['Computer Systems', 'Networks', 'Ethical Issues'],
    rating: 4.7,
    students: 720,
    image: 'https://readdy.ai/api/search-image?query=professional%20male%20teacher%20portrait%2C%20friendly%20approachable%20smile%2C%20smart%20casual%20shirt%2C%20clean%20light%20background%2C%20academic%20professional%20headshot%2C%20natural%20lighting%2C%20late%2020s%20age%2C%20realistic%20photo&width=400&height=400&seq=expert4&orientation=squarish',
    available: true,
  },
];

export default function ExpertsSection() {
  const [expanded, setExpanded] = useState<number | null>(null);
  // Temporarily hide the Meet the Experts section: keep JSX in file but don't render it.
  return (
    <>
      {false && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3 block">
                Our Team
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Meet the Experts
              </h2>
              <p className="text-slate-500 text-base max-w-xl mx-auto">
                Our tutors are experienced Computer Science teachers and examiners who know exactly what it takes to achieve top grades.
              </p>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {experts.map((expert) => (
                <div
                  key={expert.id}
                  className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-emerald-200 transition-all group"
                >
                  {/* Photo */}
                  <div className="relative w-full h-52 overflow-hidden bg-slate-100">
                    <img
                      src={expert.image}
                      alt={expert.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Availability badge */}
                    <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${expert.available ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${expert.available ? 'bg-white' : 'bg-slate-400'}`}></span>
                      {expert.available ? 'Available' : 'Fully Booked'}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <h3 className="text-base font-bold text-slate-900 mb-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {expert.name}
                    </h3>
                    <p className="text-xs font-semibold text-emerald-700 mb-1">{expert.role}</p>
                    <p className="text-xs text-slate-400 mb-3">{expert.board} · {expert.experience}</p>

                    {/* Rating & students */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs font-bold text-slate-700">{expert.rating}</span>
                      </div>
                      <span className="text-xs text-slate-400">{expert.students.toLocaleString()} students</span>
                    </div>

                    {/* Subjects */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {expert.subjects.map((s) => (
                        <span key={s} className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">{s}</span>
                      ))}
                    </div>

                    {/* Bio toggle */}
                    {expanded === expert.id && (
                      <p className="text-xs text-slate-500 leading-relaxed mb-4">{expert.bio}</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => setExpanded(expanded === expert.id ? null : expert.id)}
                        className="flex-1 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {expanded === expert.id ? 'Less' : 'View Profile'}
                      </button>
                      <button
                        onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${expert.available ? 'bg-emerald-700 hover:bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                        disabled={!expert.available}
                      >
                        {expert.available ? 'Ask AI' : 'Unavailable'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 rounded-2xl overflow-hidden relative">
              <div className="absolute inset-0" style={{ backgroundColor: '#0a1a0f' }}></div>
              <div className="relative px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <p className="text-white font-bold text-lg mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Want personalised help from our experts?
                  </p>
                  <p className="text-slate-400 text-sm">Use our AI assistant to get instant answers to your CS questions, 24/7.</p>
                </div>
                <button
                  onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
                  className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm flex-shrink-0"
                >
                  <i className="ri-robot-line text-sm"></i>
                  Chat with AI Tutor
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
